import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    CircularProgress,
    Chip,
    Button,
    ButtonGroup,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Collapse,
    Card,
    CardContent,
    Grid
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getFirestore, collection, getDocs, query as firestoreQuery, orderBy, doc, updateDoc, where } from 'firebase/firestore';
import { utils, writeFile } from 'xlsx';
import { StyledTableCell, StyledTableRow } from '../../../utils/styled';

const firestore = getFirestore();

function ListStock() {
    const [stock, setStock] = useState([]);
    const [cuadrillas, setCuadrillas] = useState([]);
    const [materiales, setMateriales] = useState([]);
    const [obras, setObras] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [vistaActual, setVistaActual] = useState('cuadrilla'); // 'cuadrilla' o 'obra'
    const [obraExpandida, setObraExpandida] = useState(null);
    const [editDialog, setEditDialog] = useState({ open: false, materialId: null, cuadrillaId: null, currentStock: 0, newStock: 0 });

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            await Promise.all([
                fetchStock(),
                fetchCuadrillas(),
                fetchObras(),
                fetchMateriales()
            ]);
        } catch (error) {
            console.error('Error cargando datos:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchStock = async () => {
        const q = firestoreQuery(collection(firestore, 'materialesxcuadrilla_huawei'));
        const querySnapshot = await getDocs(q);
        const stockData = [];
        querySnapshot.forEach((docSnap) => {
            stockData.push({ ...docSnap.data(), docId: docSnap.id });
        });
        setStock(stockData);
    };

    const fetchCuadrillas = async () => {
        const q = firestoreQuery(collection(firestore, 'cuadrillas_huawei'), orderBy('id', 'asc'));
        const querySnapshot = await getDocs(q);
        const cuadrillasData = [];
        querySnapshot.forEach((docSnap) => {
            cuadrillasData.push({ ...docSnap.data(), docId: docSnap.id });
        });
        setCuadrillas(cuadrillasData);
    };

    const fetchObras = async () => {
        const q = firestoreQuery(collection(firestore, 'obras_huawei'), orderBy('id', 'asc'));
        const querySnapshot = await getDocs(q);
        const obrasData = [];
        querySnapshot.forEach((docSnap) => {
            obrasData.push({ ...docSnap.data(), docId: docSnap.id });
        });
        setObras(obrasData);
    };

    const fetchMateriales = async () => {
        const q = firestoreQuery(collection(firestore, 'materiales_huawei'), orderBy('id', 'asc'));
        const querySnapshot = await getDocs(q);
        const materialesData = [];
        querySnapshot.forEach((docSnap) => {
            materialesData.push({ ...docSnap.data(), docId: docSnap.id });
        });
        setMateriales(materialesData);
    };

    const handleEditStock = (materialId, cuadrillaId, currentStock) => {
        setEditDialog({ 
            open: true, 
            materialId, 
            cuadrillaId, 
            currentStock, 
            newStock: currentStock 
        });
    };

    const handleCloseEditDialog = () => {
        setEditDialog({ open: false, materialId: null, cuadrillaId: null, currentStock: 0, newStock: 0 });
    };

    const handleSaveStock = async () => {
        try {
            const docRef = doc(firestore, 'materialesxcuadrilla_huawei', 
                `${editDialog.cuadrillaId}_${editDialog.materialId}`);
            await updateDoc(docRef, {
                stock: parseFloat(editDialog.newStock),
                ultimaActualizacion: Date.now()
            });
            
            // Recargar stock
            await fetchStock();
            handleCloseEditDialog();
        } catch (error) {
            console.error('Error actualizando stock:', error);
        }
    };

    const exportToExcel = () => {
        if (vistaActual === 'cuadrilla') {
            // Exportar tabla de doble entrada
            const headers = ['Material', ...cuadrillas.map(c => c.usuario ? `${c.usuario} - ${c.numero}` : c.numero)];
            const rows = materiales.map(material => {
                const row = [material.descripcion];
                cuadrillas.forEach(cuadrilla => {
                    const stockItem = stock.find(s => 
                        s.idMaterial === material.id && s.idCuadrilla === cuadrilla.id
                    );
                    row.push(stockItem ? stockItem.stock : 0);
                });
                return row;
            });

            const wsData = [headers, ...rows];
            const ws = utils.aoa_to_sheet(wsData);
            const wb = utils.book_new();
            utils.book_append_sheet(wb, ws, 'Stock por Cuadrilla');
            writeFile(wb, `Stock_Cuadrillas_Huawei_${Date.now()}.xlsx`);
        } else {
            // Exportar stock por obra
            const dataToExport = obrasFiltradasPorEstado.map(obra => {
                const stockObra = getStockPorObra(obra);
                return {
                    'Obra': obra.nombre,
                    'Localidad': obra.localidad,
                    'Estado': obra.estado,
                    'Materiales': stockObra.length,
                    'Stock Total': stockObra.reduce((sum, item) => sum + item.stock, 0)
                };
            });

            const ws = utils.json_to_sheet(dataToExport);
            const wb = utils.book_new();
            utils.book_append_sheet(wb, ws, 'Stock por Obra');
            writeFile(wb, `Stock_Obras_Huawei_${Date.now()}.xlsx`);
        }
    };

    const getStockPorObra = (obra) => {
        // Obtener IDs de cuadrillas asociadas desde el índice
        const cuadrillasAsociadas = obra.cuadrillasAsociadas || {};
        const idsCuadrillas = Object.keys(cuadrillasAsociadas).map(id => parseInt(id));
        
        if (idsCuadrillas.length === 0) return [];
        
        // Obtener stock de todas las cuadrillas asociadas
        const stockCuadrillas = stock.filter(item => 
            idsCuadrillas.includes(item.idCuadrilla)
        );
        
        // Consolidar stock por material (sumar cantidades de todas las cuadrillas)
        const stockConsolidado = {};
        stockCuadrillas.forEach(item => {
            if (!stockConsolidado[item.idMaterial]) {
                const material = materiales.find(m => m.id === item.idMaterial);
                stockConsolidado[item.idMaterial] = {
                    idMaterial: item.idMaterial,
                    codigoMaterial: item.codigoMaterial,
                    descripcionMaterial: item.descripcionMaterial || material?.descripcion,
                    stock: 0
                };
            }
            stockConsolidado[item.idMaterial].stock += item.stock;
        });
        
        return Object.values(stockConsolidado);
    };

    const obrasFiltradasPorEstado = obras.filter(obra => 
        obra.estado === 'activa' || obra.estado === 'en_proceso'
    );

    const obrasFiltradasPorBusqueda = searchTerm && vistaActual === 'obra'
        ? obrasFiltradasPorEstado.filter(obra =>
            obra.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            obra.localidad?.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : obrasFiltradasPorEstado;

    const materialesFiltrados = searchTerm && vistaActual === 'cuadrilla'
        ? materiales.filter(m =>
            m.codigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            m.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : materiales;

    if (loading) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" component="h1">
                        Stock - Huawei
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        onClick={exportToExcel}
                    >
                        Exportar a Excel
                    </Button>
                </Box>

                {/* Selector de Vista */}
                <Box sx={{ mb: 3 }}>
                    <ButtonGroup variant="outlined" fullWidth>
                        <Button
                            variant={vistaActual === 'cuadrilla' ? 'contained' : 'outlined'}
                            onClick={() => {
                                setVistaActual('cuadrilla');
                                setObraExpandida(null);
                            }}
                        >
                            Vista por Cuadrilla
                        </Button>
                        <Button
                            variant={vistaActual === 'obra' ? 'contained' : 'outlined'}
                            onClick={() => {
                                setVistaActual('obra');
                                setObraExpandida(null);
                            }}
                        >
                            Vista por Obra
                        </Button>
                    </ButtonGroup>
                </Box>

                {/* Buscador */}
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        label={vistaActual === 'obra' ? 'Buscar obra' : 'Buscar material'}
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={vistaActual === 'obra' ? 'Buscar por nombre o localidad...' : 'Buscar por código o descripción...'}
                    />
                </Box>

                {vistaActual === 'obra' ? (
                    /* Vista Maestra-Detalle para Obras */
                    <>
                        <Box sx={{ mb: 2 }}>
                            <Chip label={`${obrasFiltradasPorBusqueda.length} obras (Activas/En Proceso)`} color="primary" />
                        </Box>

                        {obrasFiltradasPorBusqueda.map((obra) => {
                            const stockObra = getStockPorObra(obra);
                            const isExpanded = obraExpandida === obra.id;
                            const totalStock = stockObra.reduce((sum, item) => sum + item.stock, 0);

                            return (
                                <Card key={obra.id} sx={{ mb: 2 }}>
                                    <CardContent
                                        sx={{ 
                                            cursor: 'pointer', 
                                            '&:hover': { backgroundColor: '#f5f5f5' },
                                            pb: isExpanded ? 2 : '16px !important'
                                        }}
                                        onClick={() => setObraExpandida(isExpanded ? null : obra.id)}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="h6">
                                                    {obra.nombre}
                                                    <Chip 
                                                        label={obra.estado === 'activa' ? 'Activa' : 'En Proceso'}
                                                        size="small"
                                                        color={obra.estado === 'activa' ? 'success' : 'info'}
                                                        sx={{ ml: 2 }}
                                                    />
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {obra.localidad} • {stockObra.length} materiales • Stock total: {totalStock}
                                                </Typography>
                                            </Box>
                                            <IconButton>
                                                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </Box>

                                        <Collapse in={isExpanded} timeout="auto">
                                            <Box sx={{ mt: 3 }}>
                                                <Typography variant="subtitle2" gutterBottom sx={{ mb: 2, color: 'primary.main' }}>
                                                    Stock Consolidado (Solo Lectura)
                                                </Typography>
                                                <TableContainer>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                                <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                                                                <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                                                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Stock</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {stockObra.map((item) => (
                                                                <TableRow key={item.idMaterial}>
                                                                    <TableCell>{item.codigoMaterial}</TableCell>
                                                                    <TableCell>{item.descripcionMaterial}</TableCell>
                                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                                        <Chip 
                                                                            label={item.stock}
                                                                            size="small"
                                                                            color={item.stock > 0 ? 'success' : 'default'}
                                                                            sx={{ fontWeight: 'bold' }}
                                                                        />
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Box>
                                        </Collapse>
                                    </CardContent>
                                </Card>
                            );
                        })}

                        {obrasFiltradasPorBusqueda.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography variant="body1" color="text.secondary">
                                    {searchTerm 
                                        ? `No se encontraron obras para "${searchTerm}"`
                                        : 'No hay obras activas o en proceso'}
                                </Typography>
                            </Box>
                        )}
                    </>
                ) : (
                    /* Vista de Tabla de Doble Entrada para Cuadrillas */
                    <TableContainer component={Paper} sx={{ maxHeight: 800 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell
                                        sx={{
                                            position: "sticky",
                                            left: 0,
                                            zIndex: 5,
                                            background: "black",
                                            color: "white",
                                            minWidth: 200
                                        }}
                                    >
                                        Material
                                    </StyledTableCell>
                                    {cuadrillas.map((cuadrilla) => (
                                        <StyledTableCell key={cuadrilla.id} sx={{ minWidth: 100 }}>
                                            {cuadrilla.usuario ? `${cuadrilla.usuario} - ${cuadrilla.numero}` : cuadrilla.numero}
                                            <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
                                                {cuadrilla.tipo === 'deposito' ? 'Dep.' : 'Cuad.'}
                                            </Typography>
                                        </StyledTableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {materialesFiltrados.map((material) => (
                                    <StyledTableRow key={material.id}>
                                        <StyledTableCell
                                            sx={{
                                                position: "sticky",
                                                left: 0,
                                                zIndex: 4,
                                                background: "#efecec",
                                                fontWeight: 'bold',
                                                minWidth: 200
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                                {material.codigo}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                {material.descripcion}
                                            </Typography>
                                        </StyledTableCell>
                                        {cuadrillas.map((cuadrilla) => {
                                            const stockItem = stock.find(s => 
                                                s.idMaterial === material.id && s.idCuadrilla === cuadrilla.id
                                            );
                                            const cantidad = stockItem ? stockItem.stock : 0;

                                            return (
                                                <StyledTableCell key={cuadrilla.id} sx={{ textAlign: 'center', padding: '4px' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                        {cantidad}
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEditStock(material.id, cuadrilla.id, cantidad)}
                                                            sx={{ ml: 0.5, padding: '2px' }}
                                                        >
                                                            <EditIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Box>
                                                </StyledTableCell>
                                            );
                                        })}
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>

            {/* Dialog para editar stock */}
            <Dialog open={editDialog.open} onClose={handleCloseEditDialog}>
                <DialogTitle>Ajustar Stock</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Material: {materiales.find(m => m.id === editDialog.materialId)?.descripcion}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Cuadrilla: {(() => {
                                            const c = cuadrillas.find(cu => cu.id === editDialog.cuadrillaId);
                                            return c?.usuario ? `${c.usuario} - ${c.numero}` : c?.numero;
                                        })()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Stock Actual: {editDialog.currentStock}
                        </Typography>
                        <TextField
                            fullWidth
                            type="number"
                            label="Nuevo Stock"
                            value={editDialog.newStock}
                            onChange={(e) => setEditDialog({ ...editDialog, newStock: e.target.value })}
                            inputProps={{ min: 0, step: 1 }}
                            sx={{ mt: 2 }}
                            autoFocus
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEditDialog}>
                        Cancelar
                    </Button>
                    <Button onClick={handleSaveStock} variant="contained" color="primary">
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default ListStock;
